import EditIcon from '@mui/icons-material/Edit';
import { Box, Grid, MenuItem, Select, Stack, styled, TextField, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import { uploadBulkConsultants } from 'wisestep-api';
import {
  WsButton,
  WsButtonGroup,
  WsColors,
  WsDataTable,
  WsSelectObj,
  WsTitleBanner,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';

import { BulkImportContext } from '../../../context/BulkImportContext';
import { visaOptions } from '../../../helper/VisaOptions';
import { LayoutTheme } from '../../../listing/components/Consultants/ConsultantsListingPage';

export const SizedTypo = styled(Typography)(() => ({
  color: WsColors.GenericRichBlack,
  fontSize: 16,
}));

const BulkUploadConfirm = () => {
  const { getConsultantData } = useContext(BulkImportContext);
  const [consultantPayloadData, setConsultantPayloadData] = useState(getConsultantData());

  const navigate = useNavigate();
  const availabilityOptions: WsSelectObj[] = [
    {
      label: 'Not Available',
      value: 'NotAvailable',
    },
    {
      label: 'Available Immediately',
      value: 'AvailableImmediately',
    },
    {
      label: 'Available in 7 days',
      value: 'AvailableIn7Days',
    },
    {
      label: 'Available In 15 Days',
      value: 'AvailableIn15Days',
    },
    {
      label: 'Available In 30 Days',
      value: 'AvailableIn30Days',
    },
  ];
  const Input = styled('input')({
    display: 'none',
  });
  const status: WsSelectObj[] = [
    {
      label: 'Prospect',
      value: 'New',
    },
    {
      label: 'Active',
      value: 'Active',
    },
    {
      label: 'Inactive',
      value: 'Inactive',
    },
  ];
  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: (cell: Cell) => (
        <Grid width={120}>
          <TextField
            name="name"
            onBlur={(e) => {
              if (e.target.value) {
                setConsultantPayloadData((prev) => {
                  prev.consultants[cell.row.index].name = e.target.value;
                  return { ...prev };
                });
              }
            }}
            type="text"
            placeholder=""
            defaultValue={cell.value}
          />
        </Grid>
      ),
    },
    {
      Header: 'Job Title',
      accessor: `jobTitle`,
      Cell: (cell: Cell) => (
        <Grid width={120}>
          <TextField
            name="jobTitle"
            onBlur={(e) => {
              if (e.target.value) {
                setConsultantPayloadData((prev) => {
                  prev.consultants[cell.row.index].jobTitle = e.target.value;
                  return { ...prev };
                });
              }
            }}
            type="text"
            placeholder=""
            defaultValue={cell.value}
          />
        </Grid>
      ),
    },
    {
      Header: 'Visa',
      accessor: 'visaStatus',
      Cell: (cell) => (
        <Grid width={100}>
          <Select
            name="visaStatus"
            defaultValue={cell.value}
            onChange={(e) => {
              setConsultantPayloadData((prev) => {
                prev.consultants[cell.row.index].visaStatus = e.target.value;
                return { ...prev };
              });
            }}
          >
            {visaOptions.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      ),
    },

    {
      Header: 'Relocation',
      accessor: 'willingToRelocate',
      Cell: (cell) => (
        <Grid width={100}>
          <Stack direction="row" sx={{ width: 100 }} spacing={1}>
            <WsButtonGroup
              inputTitle={''}
              buttonsList={['Yes', 'No']}
              selectedIndexValue={(index: number, value: string) => {
                setConsultantPayloadData((prev) => {
                  prev.consultants[cell.row.index].willingToRelocate =
                    value.toLocaleLowerCase() == 'no' || false ? false : true;
                  return { ...prev };
                });
              }}
            />
          </Stack>
        </Grid>
      ),
    },
    {
      Header: 'Experience',
      accessor: 'totalYearsExperience',
      Cell: (cell: Cell) => (
        <TextField
          name="experienceInYears"
          onBlur={(e) => {
            if (e.target.value) {
              setConsultantPayloadData((prev) => {
                prev.consultants[cell.row.index].totalYearsExperience = Number(e.target.value);
                return { ...prev };
              });
            }
          }}
          type="number"
          placeholder=""
          defaultValue={cell.value}
        />
      ),
    },
    {
      Header: 'Available',
      accessor: 'availability',
      Cell: (cell: Cell) => (
        <Select
          name="availability"
          defaultValue={cell.value}
          onChange={(e) => {
            setConsultantPayloadData((prev) => {
              prev.consultants[cell.row.index].availability = e.target.value;
              return { ...prev };
            });
          }}
        >
          {availabilityOptions.map((option) => (
            <MenuItem value={option.value} key={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      Header: 'Upload CVs',
      accessor: 'resume',
      Cell: () => (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Input id="contained-button-file" accept="image/*" multiple type="file" />
          <WsButton variant="contained">Upload CV</WsButton>
        </Stack>
      ),
    },
    {
      Header: 'Status*',
      accessor: 'status',
      Cell: (cell) => (
        <Grid width={100}>
          <Select
            name="status"
            defaultValue={cell.value}
            onChange={(e) => {
              setConsultantPayloadData((prev) => {
                prev.consultants[cell.row.index].status = e.target.value;
                return { ...prev };
              });
            }}
          >
            {status.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      ),
    },
    {
      Header: 'Confirmation',
      accessor: 'existing',
      Cell: (cell: Cell) => (
        <Box display="flex" justifyContent="space-between">
          {String(cell.value) === 'true' ? (
            <WsTypography color={WsTypoColors.ERROR}>Duplicate</WsTypography>
          ) : (
            <WsTypography color={WsTypoColors.GREEN}>New</WsTypography>
          )}
          <EditIcon fontSize="small" color="inherit" />
        </Box>
      ),
    },
  ];

  const data = consultantPayloadData.consultants;

  const onSubmit = async () => {
    try {
      await uploadBulkConsultants(consultantPayloadData);
      navigate('/consultant');
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <LayoutTheme>
      <Box>
        <WsTitleBanner
          title="Confirm Consultants"
          goBack={() => navigate('/consultant/bulk-upload-excel')}
        />

        <Box my={2}>
          <WsDataTable columns={columns} data={data as []} boldItems={['name']} />
        </Box>
        <Grid container xs={12} item justifyContent="end">
          <WsButton variant="contained" onClick={() => navigate('/consultant')} size="large">
            Cancel
          </WsButton>
          <WsButton
            variant="contained"
            onClick={() => navigate('/consultant/uploads')}
            size="large"
          >
            Back
          </WsButton>
          <WsButton variant="contained" onClick={onSubmit} size="large">
            Confirm
          </WsButton>
        </Grid>
      </Box>
    </LayoutTheme>
  );
};

export default BulkUploadConfirm;
